import { useFormik } from "formik";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import Select from "react-select";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useBackendUrl } from "../../../utils/hooks";
import ModalLoader from "../../utils/ModalLoader";
import ConfirmDialog from "../../ConfirmDialogue";

const getSubCategories = (parentCat = "All", subCategories = []) => {
  subCategories = subCategories
    .filter(el => el.Cat_Name === parentCat)
    .map(el => ({ label: el.Sub_Cat, value: el.Sub_Cat }));
  return [{ label: "All", value: "All" }, ...subCategories];
};

const filterByRegion = (region = "", data = []) => {
  return data.filter(el => el.region === region);
};

const REPRINT_STATUS_OPTIONS = ["Open", "Loaded", "Not Loaded"];

export default function ReprintConvertAtcModal({
  show,
  onHide,
  batch,
  invoiceNo,
  invoiceSetupData,
  onPrint,
}) {
  const backendUrl = useBackendUrl();
  const queryClient = useQueryClient();

  const fetchSetUpData = async () => {
    let response = await fetch(`${backendUrl}/api/incentive/set-up`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();

    data.plantSelect = data.plant.map(el => ({
      region: el.Region,
      label: el.Plant,
      value: el.Plant,
    }));

    data.depotSelect = data.depot.map(el => ({
      region: el.Region,
      label: el.Depot,
      value: el.Depot,
    }));

    data.regionSelect = data.region.map(el => ({
      label: el.region,
      value: el.region,
    }));

    data.category = data.categories.map(el => ({
      ...el,
      value: el.Cat_Name,
      label: el.Cat_Name,
    }));

    return data;
  };

  const {
    data = {
      plantSelect: [],
      depotSelect: [],
      regionSelect: [],
      category: [],
      subCategoriesWithParent: [],
    },
  } = useQuery(["ATC_INCENTIVE_SETUP"], () => fetchSetUpData(), {
    keepPreviousData: true,
    enabled: show,
  });

  const convertAtcBatch = async payload => {
    const response = await fetch(`${backendUrl}/api/atc/batches-update`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.message || "Unable to convert ATC batch");
    }

    return response.json();
  };

  const convertMutation = useMutation(payload => convertAtcBatch(payload), {
    onSuccess: result => {
      toast.success(result?.message || "ATC batch converted successfully");
      queryClient.invalidateQueries(["ATC_BATCHES_INVOICE", invoiceNo]);
      onHide();
    },
    onError: error => {
      toast.error(
        error?.message || "Unable to convert ATC batch, please try again"
      );
    },
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ATC_Code: batch?.ATC_Code || "",
      Bar_Code: batch?.Bar_Code || "",
      ATC_Status: batch?.ATC_Status || "Open",
      ATC_Plant: batch?.ATC_Plant || "",
      ATC_Depot: batch?.ATC_Depot || "",
      ATC_Region: batch?.ATC_Region || "",
      ATC_Product_Name: batch?.ATC_Product_Name || "",
      ATC_Cat_Name: batch?.ATC_Cat_Name || "",
      ATC_Sub_Cat_Name: batch?.ATC_Sub_Cat_Name || "",
      ATC_Bank: batch?.ATC_Bank || "",
      Quantity: batch?.Quantity || batch?.originalQuantity || "",
      UnitCost: batch?.UnitCost || "",
    },
    onSubmit: async values => {
      if (!REPRINT_STATUS_OPTIONS.includes(values.ATC_Status)) {
        toast.error(
          "Please select a status (Open, Loaded, or Not Loaded) before reprinting this batch."
        );
        return;
      }

      const isConfirmed = await ConfirmDialog({
        title: "Convert this batch?",
        description: `This will update ${values.ATC_Code ||
          "the batch"} with the edited details and set its status to "${
          values.ATC_Status
        }".`,
      });
      if (!isConfirmed) return;

      convertMutation.mutate({
        InvoiceNo: invoiceNo,
        batches: [
          {
            id: batch?.id,
            Trans_ID: batch?.Trans_ID,
            ATC_Code: values.ATC_Code,
            Bar_Code: values.Bar_Code,
            ATC_Status: values.ATC_Status,
            ATC_Plant: values.ATC_Plant,
            ATC_Depot: values.ATC_Depot,
            ATC_Region: values.ATC_Region,
            ATC_Product_Name: values.ATC_Product_Name,
            ATC_Cat_Name: values.ATC_Cat_Name,
            ATC_Sub_Cat_Name: values.ATC_Sub_Cat_Name,
            ATC_Bank: values.ATC_Bank,
            Quantity: values.Quantity,
          },
        ],
      });
    },
  });

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        animation={false}
        centered
        autoFocus={false}
        enforceFocus={false}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            Reprint / Convert Batch
            {batch?.ATC_Code ? ` - ${batch.ATC_Code}` : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">ATC Code</Form.Label>
                  <Form.Control
                    name="ATC_Code"
                    value={formik.values.ATC_Code}
                    onChange={formik.handleChange}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Batch</Form.Label>
                  <Form.Control
                    name="_"
                    value={
                      batch?.Bar_Code ? batch.Bar_Code.split("-").pop() : "—"
                    }
                    onChange={() => {}}
                    disabled
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Status</Form.Label>
                  <Select
                    classNamePrefix="form-select"
                    options={[
                      { label: "Open", value: "Open" },
                      { label: "Loaded", value: "Loaded" },
                      { label: "Not Loaded", value: "Not Loaded" },
                    ]}
                    value={
                      formik.values.ATC_Status
                        ? {
                            label: formik.values.ATC_Status,
                            value: formik.values.ATC_Status,
                          }
                        : null
                    }
                    onChange={selected =>
                      formik.setFieldValue("ATC_Status", selected?.value || "")
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Quantity</Form.Label>
                  <Form.Control
                    name="Quantity"
                    value={formik.values.Quantity}
                    onChange={formik.handleChange}
                    disabled
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Region</Form.Label>
                  <Select
                    classNamePrefix="form-select"
                    options={data?.regionSelect}
                    isSearchable={false}
                    value={
                      (data?.regionSelect || []).find(
                        el => el.value === formik.values.ATC_Region
                      ) || null
                    }
                    onChange={selected => {
                      formik.setFieldValue("ATC_Region", selected?.value || "");
                      formik.setFieldValue("ATC_Plant", "");
                      formik.setFieldValue("ATC_Depot", "");
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Plant</Form.Label>
                  <Select
                    classNamePrefix="form-select"
                    options={filterByRegion(
                      formik.values.ATC_Region,
                      data?.plantSelect
                    )}
                    isSearchable={false}
                    value={
                      filterByRegion(
                        formik.values.ATC_Region,
                        data?.plantSelect
                      ).find(el => el.value === formik.values.ATC_Plant) || null
                    }
                    onChange={selected => {
                      formik.setFieldValue("ATC_Plant", selected?.value || "");
                      formik.setFieldValue("ATC_Depot", "");
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Depot</Form.Label>
                  <Select
                    classNamePrefix="form-select"
                    options={filterByRegion(
                      formik.values.ATC_Region,
                      data?.depotSelect
                    )}
                    isSearchable={false}
                    value={
                      filterByRegion(
                        formik.values.ATC_Region,
                        data?.depotSelect
                      ).find(el => el.value === formik.values.ATC_Depot) || null
                    }
                    onChange={selected => {
                      formik.setFieldValue("ATC_Depot", selected?.value || "");
                      formik.setFieldValue("ATC_Plant", "");
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Product</Form.Label>
                  <Select
                    classNamePrefix="form-select"
                    options={invoiceSetupData?.invoiceCat || []}
                    value={
                      formik.values.ATC_Product_Name
                        ? {
                            label: formik.values.ATC_Product_Name,
                            value: formik.values.ATC_Product_Name,
                          }
                        : null
                    }
                    onChange={selected =>
                      formik.setFieldValue(
                        "ATC_Product_Name",
                        selected?.value || ""
                      )
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Category</Form.Label>
                  <Select
                    classNamePrefix="form-select"
                    options={data?.category}
                    value={
                      (data?.category || []).find(
                        el => el.value === formik.values.ATC_Cat_Name
                      ) || null
                    }
                    onChange={selected => {
                      formik.setFieldValue(
                        "ATC_Cat_Name",
                        selected?.value || ""
                      );
                      formik.setFieldValue("ATC_Sub_Cat_Name", "");
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Sub Category</Form.Label>
                  <Select
                    classNamePrefix="form-select"
                    options={getSubCategories(
                      formik.values.ATC_Cat_Name,
                      data?.subCategoriesWithParent
                    )}
                    value={
                      getSubCategories(
                        formik.values.ATC_Cat_Name,
                        data?.subCategoriesWithParent
                      ).find(
                        el => el.value === formik.values.ATC_Sub_Cat_Name
                      ) || null
                    }
                    onChange={selected =>
                      formik.setFieldValue(
                        "ATC_Sub_Cat_Name",
                        selected?.value || ""
                      )
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Bank</Form.Label>
                  <Select
                    classNamePrefix="form-select"
                    options={invoiceSetupData?.banks || []}
                    value={
                      formik.values.ATC_Bank
                        ? {
                            label: formik.values.ATC_Bank,
                            value: formik.values.ATC_Bank,
                          }
                        : null
                    }
                    onChange={selected =>
                      formik.setFieldValue("ATC_Bank", selected?.value || "")
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1">Unit Cost</Form.Label>
                  <Form.Control
                    name="UnitCost"
                    value={formik.values.UnitCost}
                    onChange={formik.handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer className="gap-3">
          <Button
            variant="outline-primary"
            onClick={onHide}
            disabled={convertMutation.isLoading}
          >
            Cancel
          </Button>
          {/* <Button
          variant="outline-primary"
          onClick={() => onPrint && onPrint(formik.values)}
          disabled={convertMutation.isLoading}
        >
          Reprint
        </Button> */}
          <Button
            className="px-4"
            onClick={() => formik.submitForm()}
            disabled={convertMutation.isLoading}
          >
            Reprint / Convert
          </Button>
        </Modal.Footer>
      </Modal>
      <ModalLoader show={convertMutation.isLoading} />
    </>
  );
}
